import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { ProfessionalsWorkJourneyParams } from 'domain/models';
import {
  GetAllProfessionalsSelectOptionsUseCaseInterface,
  GetProfessionalsWorkJourneyUseCaseInterface,
  HandleProfessionalsWorkJourneyUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import {
  loadFilters,
  loadProfessionalsSelectOptions,
} from 'presentation/config/store/reports/professionals-work-journey-slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { IProfessionalsWorkJourneyFilters } from '../models/professionals-work-journey-filters';

type ProfessionalsWorkJourneyFormValues = IProfessionalsWorkJourneyFilters;

type GetWorkJourneyOptions = {
  shouldUpdateButtonsLoading?: boolean;
};

export const useProfessionalsWorkJourney = () => {
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const {
    reportService,
    professionalService,
    organizationService,
    courseService,
  } = useService();

  const isManager =
    Number(currentUser?.activeProfile?.profileId) === PROFILE_TYPE.DIRECTOR;

  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [tableLoading, setTableLoading] = useState(STATUSPAGE.DONE);
  const [buttonsLoading, setButtonsLoading] = useState(STATUSPAGE.DONE);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [workJourneyData, setWorkJourneyData] =
    useState<GetProfessionalsWorkJourneyUseCaseInterface.Result>({
      total: 0,
      data: [],
    });

  const { filters, professionalsSelectOptions } = useAppSelector(
    (state) => state.professionalsWorkJourney,
  );
  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);

  const saveFilters = useCallback(
    (params: IProfessionalsWorkJourneyFilters) => {
      dispatch(loadFilters(params));
    },
    [],
  );

  const requestGetAllOrganizationsUser = useCallback(async () => {
    await organizationService
      .getAllOrganizationsUser()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => notifyError(err.message));
  }, []);

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    await courseService
      .getAllCoursesUser({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => notifyError(err.message));
  }, []);

  const requestGetAllProfessionalsSelectOptions = useCallback(
    async (params: GetAllProfessionalsSelectOptionsUseCaseInterface.Params) => {
      setProfessionalsLoading(true);

      await professionalService
        .getAllProfessionalsSelectOptions(params)
        .then((res) => dispatch(loadProfessionalsSelectOptions(res)))
        .catch((err) => notifyError(err.message))
        .finally(() => setProfessionalsLoading(false));
    },
    [],
  );

  /**
   * A API devolve o relatorio inteiro de uma vez: busca e paginacao acontecem
   * na tela, entao nenhum parametro de pagina ou busca e enviado.
   */
  const buildParams = (
    values: ProfessionalsWorkJourneyFormValues,
  ): ProfessionalsWorkJourneyParams => ({
    starts: values.starts,
    ends: values.ends,
    entity: values.entity?.value,
    course: values.course?.value,
    professional: values.professionals?.map(
      (professional) => professional.value,
    ),
  });

  const requestGetProfessionalsWorkJourney = useCallback(
    async (
      params: GetProfessionalsWorkJourneyUseCaseInterface.Params,
      options?: GetWorkJourneyOptions,
    ) => {
      setTableLoading(STATUSPAGE.LOADING);

      if (options?.shouldUpdateButtonsLoading) {
        setButtonsLoading(STATUSPAGE.LOADING);
      }

      await reportService
        .getProfessionalsWorkJourney(params)
        .then((res) => setWorkJourneyData(res))
        .catch((err) => {
          setWorkJourneyData({ total: 0, data: [] });
          notifyError(err.message);
        })
        .finally(() => {
          setTableLoading(STATUSPAGE.DONE);

          if (options?.shouldUpdateButtonsLoading) {
            setButtonsLoading(STATUSPAGE.DONE);
          }

          setHasFetchedData(true);
        });
    },
    [],
  );

  const requestHandleProfessionalsWorkJourney = useCallback(
    async (params: HandleProfessionalsWorkJourneyUseCaseInterface.Params) => {
      setButtonsLoading(STATUSPAGE.LOADING);

      await reportService
        .handleProfessionalsWorkJourney(params)
        .then((res) => {
          if (!res?.url) {
            throw new Error('URL do relatório não foi retornada.');
          }

          const isPdf = params.type === 'pdf';

          if (isPdf) {
            window.open(res.url, '_blank');
            return;
          }

          const link = document.createElement('a');
          link.href = res.url;
          link.download = 'jornada-de-trabalho-profissionais.xlsx';
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.click();
        })
        .catch((err) => notifyError(err.message))
        .finally(() => setButtonsLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const validation = useFormik<ProfessionalsWorkJourneyFormValues>({
    enableReinitialize: true,
    initialValues: {
      entity: filters?.entity || null,
      course: filters?.course || null,
      starts:
        filters?.starts || convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: filters?.ends || convertDateToYYYYMMDDFormat(getToday()),
      professionals: filters?.professionals || [],
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      starts: Yup.date().nullable().required('Data inicial e obrigatoria'),
      ends: Yup.date()
        .nullable()
        .min(Yup.ref('starts'), 'Data final deve ser maior que a inicial')
        .required('Data final e obrigatoria'),
      professionals: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
    }),
    onSubmit: (values) => {
      const params = buildParams(values);
      saveFilters(values);

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.JSON:
          requestGetProfessionalsWorkJourney(params, {
            shouldUpdateButtonsLoading: true,
          });
          break;
        case SUBMIT_BUTTON.PDF:
          requestHandleProfessionalsWorkJourney({
            ...params,
            type: 'pdf',
          });
          break;
        case SUBMIT_BUTTON.EXCEL:
          requestHandleProfessionalsWorkJourney({
            ...params,
            type: 'excel',
          });
          break;
        default:
          break;
      }
    },
  });

  useEffect(() => {
    if (isManager) {
      requestGetAllOrganizationsUser();
      requestGetAllCoursesUser();
    }
  }, [isManager]);

  useEffect(() => {
    requestGetAllProfessionalsSelectOptions({
      entity: validation.values.entity?.value,
      course: validation.values.course?.value,
    });
  }, [validation.values.entity?.value, validation.values.course?.value]);

  return {
    isManager,
    validation,
    coursesUser,
    tableLoading,
    buttonsLoading,
    hasFetchedData,
    organizationsUser,
    workJourneyData,
    professionalsLoading,
    selectedSubmitBtnRef,
    professionalsSelectOptions,
    requestGetAllCoursesUser,
  };
};
